import { Component, HostBinding, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { GetAccessToken } from './_state/auth/auth.actions';
import { Store } from '@ngxs/store';
import { AuthService } from './core/auth/auth.service';
import { QueryParamsErrorService } from './core/services/query-params-error.service';
import { NotificationsStore } from './services/user-notifications/user-notifications.store';
import { LocalStorageService } from './core/services/local-storage.service';
import { LocalStorageKeys } from './core/constants/local-storage-keys.enum';
import { TidioService } from './core/services/tidio.service';
import { RegistrationStatusService } from './core/services/registration-status.service';
import { ReferralTrackingService } from './core/services/referral-tracking.service';
import { AgencyStateService } from './core/services/agency-state.service';
import {
  CustomerIoWebService,
  CustomerIoIdentifyPayload,
  OnboardingSegment
} from './core/services/customer-io-web.service';
import { UserService } from './core/user/user.service';
import { PlansService } from './core/plans/plans.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  @HostBinding('@.disabled')
  public animationsDisabled = false;
  enableKbChatbot = environment.enableKbChatbot;

  prepareRoute(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData['animation'];
  }

  toggleAnimations() {
    this.animationsDisabled = !this.animationsDisabled;
  }
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private loader: LoadingBarService,
    private store: Store,
    private authService: AuthService,
    private queryParamsErrorService: QueryParamsErrorService,
    private notificationsStore: NotificationsStore,
    private localStorageService: LocalStorageService,
    private tidioService: TidioService,
    private registrationStatusService: RegistrationStatusService,
    private referralTrackingService: ReferralTrackingService,
    private agencyStateService: AgencyStateService,
    private customerIoWebService: CustomerIoWebService,
    private userService: UserService,
    private plansService: PlansService
  ) {}

  ngOnInit() {
    // Run localStorage migration (once)
    this.migrateLocalStorage();

    // Initialize affiliate referral tracking (cross-domain from Webflow)
    this.referralTrackingService.initialize();

    // this.fetchToken();
    // this.fetchOrders();

    // Initialize query params error handling
    this.queryParamsErrorService.initializeErrorHandling();

    // Initialize notifications if user is authenticated
    if (this.authService.accessToken && !this.authService.isDemoMode()) {
      this.notificationsStore.init(() => this.authService.accessToken);
    }

    // Initialize Customer.io web SDK (inert unless enabled + write key set)
    this.customerIoWebService.init();

    // Check registration status and agency state after authentication
    this.authService.check().subscribe((isAuth: boolean) => {
      if (isAuth) {
        this.registrationStatusService.checkStatus();
        this.agencyStateService.fetchAndUpdate();

        if (!this.authService.isDemoMode()) {
          // Tidio is the always-available "book demo / onboarding call"
          // surface for authenticated users (dashboardly-5b193), not just
          // demo mode anymore.
          this.tidioService.loadTidioChat();
          this.identifyCustomerIoUser();
        }
      } else {
        // Clear any persisted Customer.io identity for logged-out visitors
        this.customerIoWebService.reset();
        this.tidioService.removeTidioChat();
      }
    });

    // Load Tidio chat widget if user is already in demo mode (e.g., page reload)
    if (this.authService.isDemoMode()) {
      this.tidioService.loadTidioChat();
    }

    // Додавання обробника при ініціалізації
    window.addEventListener('beforeunload', () => {
      // Перевіряємо, чи в демо-режимі
      if (this.authService.isDemoMode()) {
        // Зберігаємо мітку часу останньої активності
        this.localStorageService.set(LocalStorageKeys.DEMO_LAST_ACTIVITY, Date.now().toString());
      }
    });

    // Перевірка демо-сесії при завантаженні
    this.checkDemoSession();
  }

  /**
   * Identify the authenticated user in the Customer.io web SDK so in-app
   * onboarding messages can target them (dashboardly-5b193). Uses the same
   * backend User.id the server-side Customer.io tracking identifies with.
   * No-op when the SDK is disabled; failures are swallowed silently.
   */
  private identifyCustomerIoUser(): void {
    forkJoin({
      user: this.userService.get().pipe(catchError(() => of(null))),
      planResponse: this.plansService.getCurrentPlan().pipe(catchError(() => of(null)))
    }).subscribe(({ user, planResponse }) => {
      if (!user?.id || !user?.email) {
        return;
      }

      const plan = planResponse?.data?.plan;
      const balance = planResponse?.data?.balance;
      const onboardingSegment = this.getOnboardingSegment(plan);

      const payload: CustomerIoIdentifyPayload = {
        id: user.id,
        email: user.email,
        first_name: user.username || undefined,
        plan_name: plan?.name,
        plan_id: plan?.id,
        subscription_status: balance?.subscriptionStatus || balance?.status,
        account_family: plan ? (onboardingSegment === 'agency' ? 'agency' : 'brand') : undefined,
        onboarding_eligible: onboardingSegment !== null,
        onboarding_segment: onboardingSegment || 'self_serve'
      };

      this.customerIoWebService.identify(payload);
    });
  }

  /**
   * Mirror of backend getOnboardingSegment (customer-io/constants/
   * onboarding.constants.ts): agency by planCategory/name prefix, brand
   * high-value by name (Pro, Enterprise; "Brands & Agencies" is the
   * pre-rename legacy alias of Pro). Self-serve plans return null.
   */
  private getOnboardingSegment(
    plan?: { name?: string; planCategory?: string | null } | null
  ): OnboardingSegment | null {
    if (!plan) {
      return null;
    }
    const name = plan.name || '';
    if (plan.planCategory === 'agency' || /^agency\s/i.test(name)) {
      return 'agency';
    }
    if (['Pro', 'Enterprise', 'Brands & Agencies'].includes(name)) {
      return 'brand_high_value';
    }
    return null;
  }

  private checkDemoSession() {
    // Перевіряємо тільки якщо ми в демо-режимі
    if (this.authService.isDemoMode()) {
      const lastActivity = this.localStorageService.get<string>(
        LocalStorageKeys.DEMO_LAST_ACTIVITY
      );
      if (lastActivity) {
        const inactivityPeriod = Date.now() - parseInt(lastActivity || '0');

        if (inactivityPeriod > 24 * 60 * 60 * 1000) {
          // 24 години
          // Автоматичний вихід з демо-режиму
          this.authService.demoLogout().subscribe(() => {
            // Disconnect notifications on logout
            this.notificationsStore.destroy();
          });
        }
      }
    }
  }

  /**
   * Migrate old localStorage keys to new prefixed keys (one-time migration)
   */
  private migrateLocalStorage(): void {
    const MIGRATION_FLAG = 'dashboardly_migration_completed';

    // Check if migration already completed
    if (localStorage.getItem(MIGRATION_FLAG) === 'true') {
      return;
    }

    // Map of old keys to new enum keys
    const keyMappings: Array<{ old: string; new: LocalStorageKeys }> = [
      // Auth
      { old: 'accessToken', new: LocalStorageKeys.ACCESS_TOKEN },
      { old: 'temporaryJwt', new: LocalStorageKeys.TEMPORARY_JWT },
      { old: 'completedRegistrationSteps', new: LocalStorageKeys.COMPLETED_REGISTRATION_STEPS },
      { old: 'refreshToken', new: LocalStorageKeys.REFRESH_TOKEN },
      { old: 'sellerName', new: LocalStorageKeys.SELLER_NAME },

      // Demo
      { old: 'demoMode', new: LocalStorageKeys.DEMO_MODE },
      { old: 'demoLoggedIn', new: LocalStorageKeys.DEMO_LOGGED_IN },
      { old: 'demoLastActivity', new: LocalStorageKeys.DEMO_LAST_ACTIVITY },

      // Import
      { old: 'active_import', new: LocalStorageKeys.ACTIVE_IMPORT },
      { old: 'import_last_activity', new: LocalStorageKeys.IMPORT_LAST_ACTIVITY },
      { old: 'import_active_shops', new: LocalStorageKeys.IMPORT_ACTIVE_SHOPS },
      { old: 'import_warning_shown', new: LocalStorageKeys.IMPORT_WARNING_SHOWN },
      { old: 'test_import_timeout', new: LocalStorageKeys.TEST_IMPORT_TIMEOUT },

      // UI/UX
      { old: 'subscription_banner_status', new: LocalStorageKeys.SUBSCRIPTION_BANNER_STATUS },
      { old: 'animate', new: LocalStorageKeys.ANIMATE },

      // Theme colors
      { old: 'primary_color', new: LocalStorageKeys.PRIMARY_COLOR },
      { old: 'secondary_color', new: LocalStorageKeys.SECONDARY_COLOR },
      { old: 'third_color', new: LocalStorageKeys.THIRD_COLOR },
      { old: 'sinfo_color', new: LocalStorageKeys.INFO_COLOR }, // Fix typo
      { old: 'warning_color', new: LocalStorageKeys.WARNING_COLOR },
      { old: 'light_1', new: LocalStorageKeys.LIGHT_1 }
    ];

    let migratedCount = 0;

    keyMappings.forEach(({ old, new: newKey }) => {
      const oldValue = localStorage.getItem(old);
      if (oldValue !== null) {
        // Copy to new prefixed key
        this.localStorageService.set(newKey, oldValue);
        // Remove old key
        localStorage.removeItem(old);
        migratedCount++;
      }
    });

    // Mark migration as completed
    localStorage.setItem(MIGRATION_FLAG, 'true');
  }

  fetchToken() {
    const app_key = '6e5mib5slre3f';
    const app_secret = 'fa44612254219dd9fcd0de3af1597d242cce76ea';
    const auth_code =
      'TTP_5tVLbgAAAAAtf3KhjMYjE6_dP3EEI1_5O__cBK7zWQPf-2_KU4XoAeHJkfkGb8uokv_zUAvIqhvvvDK7L05gmOzjysrsVNn8mnaSw_HyiS3HdYnSHpGFL8Mppn9les3xseIHUVMNjSDVnqeErgBW3I0dObgGtZUB-72zUkHhEtdDWRjy0rspEQOSdVt-sCMMMTQ3UHaAQjU';

    this.store.dispatch(new GetAccessToken(app_key, app_secret, auth_code));
  }
}
